import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, Save, RefreshCw, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';
const tok = () => localStorage.getItem('tp_admin_token');

const AdminPages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { slug, title, content_markdown, isNew? }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pages`, { headers: { Authorization: `Bearer ${tok()}` } });
      setItems(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load pages' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = async (slug) => {
    try {
      const r = await axios.get(`${API}/pages/${slug}`);
      setEditing({ ...r.data });
    } catch (e) {
      toast({ title: 'Failed to load page' });
    }
  };

  const startNew = () => setEditing({ slug: '', title: '', content_markdown: '', isNew: true });

  const save = async () => {
    if (!editing) return;
    const slug = (editing.slug || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (!slug) { toast({ title: 'Slug is required' }); return; }
    if (!editing.title?.trim()) { toast({ title: 'Title is required' }); return; }
    setSaving(true);
    try {
      const body = { title: editing.title, content_markdown: editing.content_markdown };
      await axios.put(`${API}/admin/pages/${slug}`, body, { headers: { Authorization: `Bearer ${tok()}` } });
      toast({ title: 'Page saved', description: `${editing.title}` });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail });
    } finally { setSaving(false); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${slug}`, { headers: { Authorization: `Bearer ${tok()}` } });
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-tp-ink flex items-center gap-2"><FileText className="text-tp-violet" size={20} /> CMS Pages</h1>
          <p className="text-sm text-gray-500">Edit Privacy Policy, Terms of Use and any custom legal/info pages (Markdown supported).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
          <Button onClick={startNew} className="btn-violet rounded-full" data-testid="add-page-btn"><Plus size={14} className="mr-1.5" /> New Page</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center border border-dashed border-gray-200">
          <FileText size={36} className="mx-auto text-gray-300 mb-3" />
          <div className="text-gray-500 text-sm">No pages yet</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map(p => (
            <div key={p.slug} data-testid={`page-row-${p.slug}`} className="bg-white rounded-2xl border border-gray-100 p-4 hover:border-tp-violet transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-tp-violet-soft text-tp-violet flex items-center justify-center shrink-0"><FileText size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-tp-ink truncate">{p.title}</div>
                  <div className="text-xs text-gray-500 font-mono">/page/{p.slug}</div>
                  {p.updated_at && (
                    <div className="text-[11px] text-gray-400 mt-1">Updated {new Date(p.updated_at).toLocaleString()}</div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-1 mt-2">
                <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" title="View public" className="p-2 text-gray-500 hover:text-tp-violet"><ExternalLink size={14} /></a>
                <button onClick={() => openEdit(p.slug)} data-testid={`edit-page-${p.slug}`} className="px-3 py-1.5 text-xs font-bold text-tp-violet hover:bg-tp-violet-soft rounded-lg">Edit</button>
                <button onClick={() => del(p.slug)} data-testid={`del-page-${p.slug}`} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText size={16} /> {editing?.isNew ? 'New Page' : `Edit · ${editing?.slug}`}</DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Slug (URL)</label>
                  <Input
                    value={editing.slug || ''}
                    disabled={!editing.isNew}
                    onChange={ev => setEditing({ ...editing, slug: ev.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder="privacy-policy"
                    data-testid="page-slug"
                  />
                  <div className="text-[11px] text-gray-400 mt-1">Public URL: <span className="font-mono">/page/{editing.slug || 'your-slug'}</span></div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Title</label>
                  <Input value={editing.title || ''} onChange={ev => setEditing({ ...editing, title: ev.target.value })} data-testid="page-title-input" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Content (Markdown supported)</label>
                <Textarea
                  rows={20}
                  value={editing.content_markdown || ''}
                  onChange={ev => setEditing({ ...editing, content_markdown: ev.target.value })}
                  className="font-mono text-xs"
                  data-testid="page-content"
                />
                <div className="text-[11px] text-gray-400 mt-1">
                  Use <code className="bg-gray-100 px-1 rounded">## Heading</code>, <code className="bg-gray-100 px-1 rounded">**bold**</code>, <code className="bg-gray-100 px-1 rounded">- bullet</code>, <code className="bg-gray-100 px-1 rounded">[link](url)</code>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-violet rounded-full" data-testid="save-page-btn">
              {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Save size={14} className="mr-1.5" />} Save Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPages;
